import {
  getRequestLanguage,
  isApiOriginRequest,
  isMutatingMethod,
  parseCsrfTokenFromResponse,
} from "./invokeApiUtils";

const CSRF_HEADER = "X-CSRF-Token";

/**
 * Double-submit CSRF for the XS Learnix App API: GET token + cookie, then send `X-CSRF-Token` on mutations.
 * @param {object} opts
 * @param {import("axios").AxiosInstance} opts.axios
 * @param {string} opts.baseUrl
 * @param {string} opts.appContext - `X-App-Context` header for the CSRF fetch
 * @param {string} [opts.csrfFetchPath] - default `api/general_routes/csrf_token`
 */
export function createCsrfTokenManager({
  axios,
  baseUrl,
  appContext,
  csrfFetchPath = "api/general_routes/csrf_token",
}) {
  let csrfToken = null;
  let csrfFetchPromise = null;

  function clearCsrfToken() {
    csrfToken = null;
  }

  async function ensureCsrfToken() {
    if (csrfToken) return csrfToken;
    if (!csrfFetchPromise) {
      csrfFetchPromise = axios
        .get(`${baseUrl}${csrfFetchPath}`, {
          withCredentials: true,
          headers: {
            "X-App-Context": appContext,
            "Accept-Language": getRequestLanguage(),
            "X-Language": getRequestLanguage(),
          },
        })
        .then((res) => {
          csrfToken = parseCsrfTokenFromResponse(res.data);
          return csrfToken;
        })
        .finally(() => {
          csrfFetchPromise = null;
        });
    }
    return csrfFetchPromise;
  }

  async function requestInterceptor(config) {
    const method = (config.method || "get").toUpperCase();
    if (!isMutatingMethod(method)) {
      return config;
    }
    const url = typeof config.url === "string" ? config.url : "";
    if (!isApiOriginRequest(url, baseUrl)) {
      return config;
    }
    if (url.includes("csrf_token")) {
      return config;
    }
    const token = await ensureCsrfToken();
    config.headers = config.headers || {};
    config.headers[CSRF_HEADER] = token;
    return config;
  }

  return { clearCsrfToken, ensureCsrfToken, requestInterceptor };
}
